import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import MediatorManager from "../../../core/application/MediatorManager";
import Config from "../../../core/config/Config";
import SoundType from "../../../core/sound/util/SoundType";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import ClickEaterFactory from "../../../core/ui/ClickEaterFactory";
import SkinKeyRegistry from "../../../core/ui/SkinKeyRegistry";
import ScormController from "../../../scorm/controller/ScormController";
import CharacterLibrary from "../../characters/model/CharacterLibrary";
import PlatformUserTrackingService from "../../platform/controller/PlatformUserTrackingService";
import PlayerModel from "../../player/model/PlayerModel";
import ProgressModel from "../../progress/model/ProgressModel";
import SaveGameEvent from "../../savegame/event/SaveGameEvent";
import ScoreService from "../../score/controller/ScoreService";
import PromiseUtil from "../../util/PromiseUtil";
import EndOfGameScreenOverviewMediator from "./EndOfGameScreenOverviewMediator";
import EndOfGameScreenScoreMediator from "./EndOfGameScreenScoreMediator";

export default class EndOfGameScreenMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {ClickEaterFactory} clickEaterFactory 
     * @param {PlayerModel} playerModel
     * @param {RevisableLanguagePack} languagePack 
     * @param {Config} config 
     * @param {ScormController} scormController 
     * @param {ProgressModel} progressModel 
     * @param {CharacterLibrary} characterLibrary 
     * @param {MediatorManager} mediatorManager 
     * @param {ScoreService} scoreService 
     * @param {SoundManager} soundManager 
     * @param {PlatformUserTrackingService} platformUserTrackingService 
     */
    constructor(target, dispatcher, layoutManager, clickEaterFactory, playerModel, languagePack, config, scormController, progressModel, characterLibrary,
        mediatorManager, scoreService, soundManager, platformUserTrackingService) {
        super(target, dispatcher, layoutManager, clickEaterFactory);

        this._playerModel = playerModel;
        this._languagePack = languagePack;
        this._config = config;
        this._scormController = scormController;
        this._progressModel = progressModel;
        this._characterLibrary = characterLibrary;
        this._mediatorManager = mediatorManager;
        this._scoreService = scoreService;
        this._soundManager = soundManager;
        this._platformUserTrackingService = platformUserTrackingService;

        this._statusId = ``;
    }

    initialize() {
        super.initialize();

        const trackerScores = this._scoreService.getTrackerScores();
        const primaryTrackerScore = this._scoreService.getPrimaryTrackerScore();
        const status = this._scoreService.getStatusScore();

        this._trackScormCompletion();
        this._platformUserTrackingService.complete({
            groupSpiritScore: trackerScores.find(trackerScore => trackerScore.id === "groupSpirit").percentage,
            trustScore: trackerScores.find(trackerScore => trackerScore.id === "trust").percentage,
        });

        /** @type {EndOfGameScreenScoreMediator} */
        this._scorePanel = this._mediatorManager.createMediator(this._target.scorePanel, SkinKeyRegistry.END_OF_GAME_SCORE_PANEL);
        this._scorePanel.populate({
            trackerScores,
            primaryTrackerScore,
        });
        this._scorePanel.hideInstant();

        /** @type {EndOfGameScreenOverviewMediator} */
        this._overviewPanel = this._mediatorManager.createMediator(this._target.overviewPanel, SkinKeyRegistry.END_OF_GAME_OVERVIEW_PANEL);
        this._overviewPanel.populate({
            trackerScores,
            primaryTrackerScore,
            status,
        });
        this._overviewPanel.hideInstant();

        this._startAnimations();

        this.preventClickThrough();
    }

    destroy() {
        super.destroy();

        // Sub-mediators.
        this._scorePanel.destroy();
        this._scorePanel = null;
        this._overviewPanel.destroy();
        this._overviewPanel = null;
    }

    async _startAnimations() {
        this._soundManager.playSound(SoundType.END_OF_GAME_TRANSITION);
        await this._scorePanel.show();
        await PromiseUtil.delay(2050);

        this._scorePanel.transitionPartiallyOut();
        await this._overviewPanel.show();
        await PromiseUtil.delay(4000);
        this.dispatch(SaveGameEvent.SHOW_PENDING_FEEDBACK);
    }

    _trackScormCompletion() {
        this._scormController.complete();
        this._scormController.save();
    }
}